import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyCredits } from "@/hooks/useCompanyCredits";
import { useDualCredits } from "@/hooks/useDualCredits";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Zap, CircleDot, TrendingDown, TrendingUp, Minus, Lock, Loader2,
  BarChart3, Activity, History, Target,
} from "lucide-react";

const UNLOCK_COST = 5;
const LANE_LOOKBACK_DAYS = 90;

interface Bid {
  id: string;
  bid_amount: number;
  submitted_at: string;
  status: string;
  ready_to_cover?: boolean | null;
  truck_in_area?: boolean | null;
  team_available?: boolean | null;
}

interface LoadCtx {
  id: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  total_miles: number | null;
  target_rate_min: number | null;
  target_rate_max: number | null;
}

interface Props {
  load: LoadCtx;
  bids: Bid[];
}

interface LaneHistory {
  loadCount: number;
  bidCount: number;
  avgRpm: number | null;
  lowRpm: number | null;
  highRpm: number | null;
}

function fmt$(n: number) { return `$${Math.round(n).toLocaleString()}`; }
function fmtRpm(n: number | null) { return n == null ? "—" : `$${n.toFixed(2)}/mi`; }

export function MarketInsightsPanel({ load, bids }: Props) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId } = useCompanyCredits();
  const { totalAvailable, loading: creditsLoading } = useDualCredits();

  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [spending, setSpending] = useState(false);
  const [lane, setLane] = useState<LaneHistory | null>(null);
  const [laneLoading, setLaneLoading] = useState(false);

  const refKey = `market_insights_${load.id}`;

  // Check if this load's insights were previously unlocked (persist per-load)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking(true);
      const { data } = await supabase
        .from("credit_transactions")
        .select("id")
        .eq("reference_key", refKey)
        .limit(1)
        .maybeSingle();
      if (!cancelled) {
        setUnlocked(!!data);
        setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refKey]);

  // Fetch lane history once unlocked
  useEffect(() => {
    if (!unlocked) return;
    let cancelled = false;
    (async () => {
      setLaneLoading(true);
      const since = new Date(Date.now() - LANE_LOOKBACK_DAYS * 86400 * 1000).toISOString();
      const { data: loads } = await supabase
        .from("direct_loads" as any)
        .select("id, total_miles")
        .eq("origin_city", load.origin_city)
        .eq("origin_state", load.origin_state)
        .eq("destination_city", load.destination_city)
        .eq("destination_state", load.destination_state)
        .eq("equipment_type", load.equipment_type)
        .neq("id", load.id)
        .gte("created_at", since)
        .limit(100);

      const loadRows = (loads as any[]) || [];
      const idMiles = new Map<string, number>();
      loadRows.forEach((l) => { if (l.total_miles && l.total_miles > 0) idMiles.set(l.id, l.total_miles); });

      let bidCount = 0;
      const rpms: number[] = [];
      if (idMiles.size > 0) {
        const { data: laneBids } = await supabase
          .from("direct_bids" as any)
          .select("direct_load_id, bid_amount")
          .in("direct_load_id", Array.from(idMiles.keys()));
        (laneBids as any[] || []).forEach((b) => {
          bidCount++;
          const m = idMiles.get(b.direct_load_id);
          if (m && b.bid_amount) rpms.push(b.bid_amount / m);
        });
      }
      const avg = rpms.length ? rpms.reduce((s, r) => s + r, 0) / rpms.length : null;
      const low = rpms.length ? Math.min(...rpms) : null;
      const high = rpms.length ? Math.max(...rpms) : null;

      if (!cancelled) {
        setLane({ loadCount: loadRows.length, bidCount, avgRpm: avg, lowRpm: low, highRpm: high });
        setLaneLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [unlocked, load.id, load.origin_city, load.origin_state, load.destination_city, load.destination_state, load.equipment_type]);

  const stats = useMemo(() => {
    const pending = bids.filter(b => b.status === "pending").sort((a, b) => a.bid_amount - b.bid_amount);
    if (pending.length === 0) return null;
    const amounts = pending.map(b => b.bid_amount);
    const low = amounts[0];
    const high = amounts[amounts.length - 1];
    const avg = amounts.reduce((s, a) => s + a, 0) / amounts.length;
    const spread = high - low;
    const spreadPct = avg > 0 ? (spread / avg) * 100 : 0;
    const miles = load.total_miles && load.total_miles > 0 ? load.total_miles : null;

    // Tightening: compare stddev of first half vs second half (chronological)
    const chrono = [...pending].sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());
    let tightening: "tightening" | "widening" | "stable" | null = null;
    if (chrono.length >= 4) {
      const mid = Math.floor(chrono.length / 2);
      const first = chrono.slice(0, mid).map(b => b.bid_amount);
      const second = chrono.slice(mid).map(b => b.bid_amount);
      const stddev = (arr: number[]) => {
        const m = arr.reduce((s, v) => s + v, 0) / arr.length;
        return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
      };
      const s1 = stddev(first), s2 = stddev(second);
      if (s1 > 0) {
        const ratio = s2 / s1;
        if (ratio < 0.75) tightening = "tightening";
        else if (ratio > 1.25) tightening = "widening";
        else tightening = "stable";
      }
    }

    // Velocity: bids per hour over the last hour vs prior hour
    const now = Date.now();
    const lastHour = chrono.filter(b => now - new Date(b.submitted_at).getTime() < 3600_000).length;
    const priorHour = chrono.filter(b => {
      const dt = now - new Date(b.submitted_at).getTime();
      return dt >= 3600_000 && dt < 7200_000;
    }).length;

    const confidenceHits = pending.filter(b => b.ready_to_cover || b.truck_in_area || b.team_available).length;

    return { count: pending.length, low, high, avg, spread, spreadPct, miles, tightening, lastHour, priorHour, confidenceHits, low_rpm: miles ? low / miles : null, high_rpm: miles ? high / miles : null, avg_rpm: miles ? avg / miles : null };
  }, [bids, load.total_miles]);

  const handleUnlock = async () => {
    if (spending) return;
    if (!companyId) {
      toast({ title: "Company required", description: "You need to be on a company account to unlock insights.", variant: "destructive" });
      return;
    }
    if (!creditsLoading && totalAvailable < UNLOCK_COST) {
      toast({ title: "Not enough credits", description: `You need ${UNLOCK_COST} credits. Redirecting to buy more.` });
      navigate("/company-settings?tab=credits");
      return;
    }
    setSpending(true);
    try {
      const { data, error } = await supabase.rpc("spend_credits_dual", {
        p_company_id: companyId,
        p_amount: UNLOCK_COST,
        p_type: "feature_unlock",
        p_description: `Market Insights unlock — Load ${load.id.slice(0, 8)}`,
        p_reference_key: refKey,
        p_metadata: { feature: "market_insights", direct_load_id: load.id } as any,
      });
      if (error) throw error;
      const result: any = typeof data === "string" ? JSON.parse(data) : data;
      if (result?.success) {
        setUnlocked(true);
        if (result.idempotent) {
          toast({ title: "Already unlocked", description: "No credits charged." });
        } else {
          toast({ title: "Insights unlocked", description: `${UNLOCK_COST} credits used.` });
        }
      } else {
        toast({ title: "Not enough credits", description: `You need ${UNLOCK_COST} credits. Redirecting to buy more.` });
        navigate("/company-settings?tab=credits");
      }
    } catch (e: any) {
      console.error("Market Insights unlock failed:", e);
      toast({ title: "Unlock failed", description: e.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setSpending(false);
    }
  };

  // ── LOCKED VIEW ──
  if (!unlocked) {
    return (
      <div className="border border-border rounded-lg bg-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-4 w-4 text-brand-teal" />
          <h4 className="text-sm font-semibold text-foreground">Market Insights</h4>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Know where the market stands before you award
        </p>
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          <li className="flex items-center gap-1.5"><CircleDot className="h-3 w-3 text-brand-teal flex-shrink-0" />Bid pool tightening & velocity</li>
          <li className="flex items-center gap-1.5"><CircleDot className="h-3 w-3 text-brand-teal flex-shrink-0" />Low / avg / high on this load ($ &amp; $/mi)</li>
          <li className="flex items-center gap-1.5"><CircleDot className="h-3 w-3 text-brand-teal flex-shrink-0" />This lane's bid history on LaneRunner</li>
        </ul>
        <Button
          onClick={handleUnlock}
          disabled={spending || checking}
          size="sm"
          className="w-full mt-3 text-xs bg-brand-teal hover:bg-brand-teal-deep text-white"
        >
          {spending ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Unlocking…</>
            : <><Lock className="h-3 w-3 mr-1.5" /> Unlock for {UNLOCK_COST} credits</>}
        </Button>
      </div>
    );
  }

  // ── UNLOCKED VIEW ──
  const trendIcon = stats?.tightening === "tightening" ? TrendingDown
    : stats?.tightening === "widening" ? TrendingUp : Minus;
  const trendColor = stats?.tightening === "tightening" ? "text-emerald-600"
    : stats?.tightening === "widening" ? "text-amber-600" : "text-muted-foreground";
  const trendLabel = stats?.tightening === "tightening" ? "Bids tightening"
    : stats?.tightening === "widening" ? "Bids widening" : stats?.tightening === "stable" ? "Stable spread" : "Need more bids";

  const budgetMax = load.target_rate_max;
  const budgetHint = stats && budgetMax
    ? stats.low <= budgetMax
      ? { text: `Low bid ${fmt$(budgetMax - stats.low)} under budget`, positive: true }
      : { text: `Low bid ${fmt$(stats.low - budgetMax)} over budget`, positive: false }
    : null;

  return (
    <div className="border border-brand-teal/30 rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-brand-teal" />
          <h4 className="text-sm font-semibold text-foreground">Market Insights</h4>
        </div>
        <span className="text-[10px] text-muted-foreground">Unlocked</span>
      </div>

      {!stats ? (
        <p className="text-xs text-muted-foreground">No pending bids yet — insights populate as bids arrive.</p>
      ) : (
        <>
          {/* This load: distribution */}
          <div className="rounded-md bg-muted/40 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <BarChart3 className="h-3.5 w-3.5 text-brand-teal" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">This Load · {stats.count} {stats.count === 1 ? "bid" : "bids"}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] text-muted-foreground">Low</p>
                <p className="text-sm font-bold text-foreground tabular-nums">{fmt$(stats.low)}</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">{fmtRpm(stats.low_rpm)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Avg</p>
                <p className="text-sm font-bold text-brand-teal tabular-nums">{fmt$(stats.avg)}</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">{fmtRpm(stats.avg_rpm)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">High</p>
                <p className="text-sm font-bold text-foreground tabular-nums">{fmt$(stats.high)}</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">{fmtRpm(stats.high_rpm)}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Spread</span>
              <span className="font-semibold text-foreground tabular-nums">{fmt$(stats.spread)} · {stats.spreadPct.toFixed(0)}%</span>
            </div>
          </div>

          {/* Trend + velocity */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-border p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Activity className={`h-3 w-3 ${trendColor}`} />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Trend</span>
              </div>
              <div className={`flex items-center gap-1 ${trendColor}`}>
                {(() => { const I = trendIcon; return <I className="h-3.5 w-3.5" />; })()}
                <span className="text-xs font-semibold">{trendLabel}</span>
              </div>
            </div>
            <div className="rounded-md border border-border p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="h-3 w-3 text-brand-teal" />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Velocity</span>
              </div>
              <p className="text-xs font-semibold text-foreground tabular-nums">
                {stats.lastHour} last hr <span className="text-muted-foreground font-normal">· {stats.priorHour} prior</span>
              </p>
            </div>
          </div>

          {/* Budget + confidence */}
          <div className="grid grid-cols-2 gap-2">
            {budgetHint && (
              <div className="rounded-md border border-border p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Target className="h-3 w-3 text-brand-teal" />
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">vs Budget</span>
                </div>
                <p className={`text-xs font-semibold ${budgetHint.positive ? "text-emerald-600" : "text-amber-600"}`}>
                  {budgetHint.text}
                </p>
              </div>
            )}
            <div className="rounded-md border border-border p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <CircleDot className="h-3 w-3 text-brand-teal" />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Signals</span>
              </div>
              <p className="text-xs font-semibold text-foreground tabular-nums">
                {stats.confidenceHits}/{stats.count} <span className="text-muted-foreground font-normal">confidence</span>
              </p>
            </div>
          </div>

          {/* Lane history — LaneRunner platform data only */}
          <div className="rounded-md bg-muted/40 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <History className="h-3.5 w-3.5 text-brand-teal" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">This Lane · last {LANE_LOOKBACK_DAYS}d</span>
            </div>
            {laneLoading ? (
              <p className="text-[11px] text-muted-foreground">Loading lane history…</p>
            ) : !lane || lane.bidCount === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No prior LaneRunner bids on {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state} for {load.equipment_type}.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Low</p>
                    <p className="text-sm font-bold text-foreground tabular-nums">{fmtRpm(lane.lowRpm)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Avg</p>
                    <p className="text-sm font-bold text-brand-teal tabular-nums">{fmtRpm(lane.avgRpm)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">High</p>
                    <p className="text-sm font-bold text-foreground tabular-nums">{fmtRpm(lane.highRpm)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Based on {lane.bidCount} bids across {lane.loadCount} LaneRunner loads.
                </p>
              </>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground italic">
            Insights use LaneRunner platform data only — no external benchmarks.
          </p>
        </>
      )}
    </div>
  );
}
